import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  BooleanStrict,
  GetTimelyDataDiffIdQuerySchema,
  GetTimelyDataDiffScopingQuerySchema,
  OKGeneric,
  PresentableTimelyDataDiffSchema,
  ProcessedTimelyDataDiffCallbackSchema,
  ReadTimelyDataDiffIndexSchema,
  ResourceBaseSchema,
  ResourceMetadataWriteSchema,
} from '../../models';

extendZodWithOpenApi(z);
const c = initContract();

export const apiTimelyResourcesRoute = c.router({
  getTimelyDataDiffsRecentlyGenerated: {
    method: 'GET',
    path: `/1.0/timely/data_diffs/recent_diffs`,
    query: GetTimelyDataDiffScopingQuerySchema.omit({ connectionId: true }),
    summary: 'Get an overview of the last 100 data diffs',
    description:
      'Retrieves the most recent 100 data diffs for the specified Timely account. Returns an index of diffs with their status, timestamps, and resource counts. Useful for monitoring sync health and debugging data export issues.',
    responses: {
      200: z.array(ReadTimelyDataDiffIndexSchema),
    },
  },
  getTimelyDataDiffNextForProcessing: {
    method: 'GET',
    path: `/1.0/timely/data_diffs/next_diff`,
    summary: 'Returns the next data diff to be processed by Timely.',
    query: GetTimelyDataDiffScopingQuerySchema.omit({ connectionId: true }).extend({
      setAsInProgress: BooleanStrict.default(true).openapi({
        example: true,
        description:
          'Assumes the diff will be worked on immediately, marking the diff as "in_progress"',
      }),
    }),
    responses: {
      200: PresentableTimelyDataDiffSchema,
    },
    description: `
By default, the diff is marked as \`status: 'in_progress'\` when requested.
Disable with \`?setAsInProgress=false\`.

\`in_progress\` diffs _will still return as "next" diff_ until Timely has processed
them and reported back to \`callbackAfterProcessedTimelyDataDiffId\`.
  `,
  },
  getTimelyDataDiffId: {
    method: 'GET',
    path: `/1.0/timely/data_diffs/by_id/:diffId`,
    pathParams: GetTimelyDataDiffIdQuerySchema,
    summary: 'Retrieve one specific exported data diff by ID',
    description:
      'Fetches the complete details of a specific data diff by its UUID. Returns the full diff payload including all resources, identities, and processing metadata. Used by Timely to retrieve and re-process specific diffs.',
    responses: {
      200: PresentableTimelyDataDiffSchema,
    },
  },
  callbackAfterProcessedTimelyDataDiffId: {
    method: 'POST',
    path: `/1.0/timely/data_diffs/post_process_callback`,
    summary: 'Store logs and outcome once Timely is done processing a data dump/diff',
    description:
      'Callback endpoint for Timely to report the result of processing a data diff. Stores success/failure status, processing details, and any errors. Updates the diff record to mark it as processed and logs activity for monitoring.',
    body: ProcessedTimelyDataDiffCallbackSchema,
    responses: {
      200: OKGeneric,
    },
  },
  getResourceMetadataByUri: {
    method: 'POST',
    path: `/1.0/timely/resources/metadata`,
    body: z.object({
      uris: z
        .array(z.string().url())
        .min(1)
        .max(100)
        .describe('Array of resource URIs to fetch metadata for. Min 1, Max 100 URIs per request.'),
    }),
    summary: 'Get resource metadata (Title, ID, URL) by URIs for syncing to Timely',
    description: `
Retrieves metadata for TIC resources by their URIs. Used by Timely backend to fetch
resource details for syncing linked work data (TT-4388).

Returns only found resources. Resources not found are omitted from the response.

Supports batch fetching of 1-100 resources in a single request. Uses POST method to avoid
URL length limitations with large batches.

**Authentication**: Requires \`INTERNAL_TIMELY_WEBHOOK_API_KEY\` in Authorization header.
    `,
    responses: {
      200: z.array(
        ResourceBaseSchema.pick({ uri: true, title: true, url_html: true }).extend({
          external_code: ResourceMetadataWriteSchema.shape.external_code,
        })
      ),
    },
  },
});
